"use client";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { useTranslations } from "next-intl";
import type { HTMLAttributes, ReactNode } from "react";
import * as React from "react";
import type { FieldPath, FieldValues } from "react-hook-form";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { readFileAsDataURL } from "@/lib/utils/crop-image";
import {
  formatAllowedFormats,
  formatFileSize,
  getServerPreviewUrl,
  isFile,
  isFileArray,
  isImageFile,
  revokeObjectUrl,
} from "@/lib/utils/file-utils";
import { Button } from "../button";
import { ImageCropDialog } from "../image-crop-dialog";
import {
  type ImageItem,
  MultiImageCropDialog,
} from "../image-crop-dialog/MultiImageCropDialog";
import { XIcon } from "../XIcon";
import { SortableImageItem } from "./sortable-image-item";

interface FileUploaderContentProps<TFieldValues extends FieldValues> {
  field: {
    value: TFieldValues[FieldPath<TFieldValues>];
    onChange: (value: TFieldValues[FieldPath<TFieldValues>]) => void;
  };
  fieldState: { error?: { message?: string } };
  multiple: boolean;
  allowedFormats: string[];
  maxSize: number;
  accept?: string;
  icon: ReactNode;
  title: string;
  buttonText: string;
  loading?: boolean;
  required?: boolean;
  helperText?: string;
  containerClass?: Pick<HTMLAttributes<HTMLDivElement>, "className">;
}

export function FileUploaderContent<TFieldValues extends FieldValues>({
  field,
  fieldState: { error },
  multiple,
  allowedFormats,
  maxSize,
  accept,
  icon,
  title,
  buttonText,
  loading,
  required,
  helperText,
  containerClass,
}: FileUploaderContentProps<TFieldValues>) {
  const t = useTranslations("common");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [previewUrls, setPreviewUrls] = React.useState<Map<File, string>>(
    new Map(),
  );
  const [activeDeleteButtonIndex, setActiveDeleteButtonIndex] = React.useState<
    number | null
  >(null);
  const imagesContainerRef = React.useRef<HTMLDivElement>(null);
  const deleteButtonTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Multi-image crop dialog state
  const [multiCropDialogOpen, setMultiCropDialogOpen] = React.useState(false);
  const [imagesToCrop, setImagesToCrop] = React.useState<ImageItem[]>([]);

  // Single image edit state
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingImage, setEditingImage] = React.useState<{
    index: number;
    originalUrl: string;
    fileName: string;
  } | null>(null);

  // Store original images for re-editing (keyed by cropped file reference)
  const [originalImages, setOriginalImages] = React.useState<Map<File, string>>(
    new Map(),
  );

  // Check if we're dealing with image files
  const isImageUploader = allowedFormats.some((format) =>
    ["jpg", "jpeg", "png", "webp", "gif"].includes(format.toLowerCase()),
  );

  // Handle showing delete button for a specific image
  const handleShowDeleteButton = (index: number) => {
    setActiveDeleteButtonIndex(index);

    if (deleteButtonTimeoutRef.current) {
      clearTimeout(deleteButtonTimeoutRef.current);
    }

    deleteButtonTimeoutRef.current = setTimeout(() => {
      setActiveDeleteButtonIndex(null);
    }, 10000);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (deleteButtonTimeoutRef.current) {
        clearTimeout(deleteButtonTimeoutRef.current);
      }
    };
  }, []);

  // Handle clicks outside images container
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        imagesContainerRef.current &&
        !imagesContainerRef.current.contains(e.target as Node)
      ) {
        setActiveDeleteButtonIndex(null);
        if (deleteButtonTimeoutRef.current) {
          clearTimeout(deleteButtonTimeoutRef.current);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // DnD Kit sensors
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 150, tolerance: 5 },
  });
  const sensors = useSensors(pointerSensor, touchSensor);

  const validateFile = (file: File): string | null => {
    const fileExtension = file.name?.split(".").pop()?.toLowerCase();
    const isValidFormat = allowedFormats.some(
      (format) => format.toLowerCase() === fileExtension?.toLowerCase(),
    );

    if (!isValidFormat) {
      return t("fileUploader.invalidFormat", {
        formats: formatAllowedFormats(allowedFormats),
      });
    }

    if (isImageUploader && isImageFile(file)) {
      return null;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      return t("fileUploader.tooLarge", { maxSize });
    }

    return null;
  };

  // Process files and open multi-crop dialog
  const processFilesForCropping = async (files: File[]) => {
    const imageItems: ImageItem[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (isImageFile(file)) {
        try {
          const dataUrl = await readFileAsDataURL(file);
          imageItems.push({
            id: `${file.name}-${file.size}-${Date.now()}-${i}`,
            originalUrl: dataUrl,
            fileName: file.name,
          });
        } catch (error) {
          console.error("Error reading file:", error);
        }
      }
    }

    if (imageItems.length > 0) {
      setImagesToCrop(imageItems);
      setMultiCropDialogOpen(true);
    }
  };

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    fileArray.forEach((file) => {
      const err = validateFile(file);
      if (err) {
        validationErrors.push(err);
      } else {
        validFiles.push(file);
      }
    });

    if (validationErrors.length > 0) {
      validationErrors.forEach((err) => {
        toast.error(err);
      });
    }

    if (validFiles.length === 0) return;

    // Check if these are image files that need cropping
    const imageFiles = validFiles.filter((file) => isImageFile(file));
    const nonImageFiles = validFiles.filter((file) => !isImageFile(file));

    // Add non-image files directly
    if (nonImageFiles.length > 0) {
      if (multiple) {
        const currentValue = isFileArray(field.value) ? field.value : [];
        field.onChange([
          ...currentValue,
          ...nonImageFiles,
        ] as typeof field.value);
      } else if (nonImageFiles[0]) {
        field.onChange(nonImageFiles[0] as typeof field.value);
      }
    }

    // Process image files through cropper
    if (isImageUploader && imageFiles.length > 0) {
      await processFilesForCropping(imageFiles);
    }
  };

  // Handle multi-crop completion
  const handleMultiCropComplete = React.useCallback(
    (croppedFiles: File[], originalUrls: string[]) => {
      const acceptedFiles = croppedFiles.filter((file) => {
        const fileSizeMB = file.size / (1024 * 1024);

        if (fileSizeMB <= maxSize) {
          return true;
        }

        toast.error(t("fileUploader.stillTooLarge", { maxSize }));
        return false;
      });

      if (acceptedFiles.length === 0) {
        setImagesToCrop([]);
        return;
      }

      // Store original URLs for each cropped file
      setOriginalImages((prev) => {
        const newMap = new Map(prev);
        acceptedFiles.forEach((file, index) => {
          newMap.set(file, originalUrls[index]);
        });
        return newMap;
      });

      // Add cropped files to form
      if (multiple) {
        const currentValue = isFileArray(field.value) ? field.value : [];
        field.onChange([
          ...currentValue,
          ...acceptedFiles,
        ] as typeof field.value);
      } else if (acceptedFiles[0]) {
        field.onChange(acceptedFiles[0] as typeof field.value);
      }

      setImagesToCrop([]);
    },
    [field, maxSize, multiple, t],
  );

  // Handle edit button click on existing image
  const handleEditImage = React.useCallback(
    async (index: number) => {
      if (!isFileArray(field.value)) return;

      const files: File[] = field.value;
      const file = files[index];

      if (file && isImageFile(file)) {
        // Check if we have the original image stored
        const originalUrl =
          originalImages.get(file) ?? getServerPreviewUrl(file);

        if (originalUrl) {
          setEditingImage({
            index,
            originalUrl,
            fileName: file.name,
          });
          setEditDialogOpen(true);
        } else {
          // Fallback: use the cropped file
          try {
            const dataUrl = await readFileAsDataURL(file);
            setOriginalImages((prev) => {
              const newMap = new Map(prev);
              newMap.set(file, dataUrl);
              return newMap;
            });
            setEditingImage({
              index,
              originalUrl: dataUrl,
              fileName: file.name,
            });
            setEditDialogOpen(true);
          } catch (error) {
            console.error("Error reading file for edit:", error);
          }
        }
      }
    },
    [field.value, originalImages],
  );

  // Handle single image edit complete
  const handleEditCropComplete = React.useCallback(
    (croppedFile: File) => {
      if (editingImage === null) return;
      const fileSizeMB = croppedFile.size / (1024 * 1024);

      if (fileSizeMB > maxSize) {
        toast.error(t("fileUploader.stillTooLarge", { maxSize }));
        return;
      }

      if (isFileArray(field.value)) {
        const currentFiles: File[] = [...field.value];
        const oldFile = currentFiles[editingImage.index];

        // Transfer the original URL from old file to new cropped file
        setOriginalImages((prev) => {
          const newMap = new Map(prev);
          if (oldFile) {
            newMap.delete(oldFile);
          }
          newMap.set(croppedFile, editingImage.originalUrl);
          return newMap;
        });

        currentFiles[editingImage.index] = croppedFile;
        field.onChange(currentFiles as typeof field.value);
      }

      setEditingImage(null);
      setEditDialogOpen(false);
    },
    [editingImage, field, maxSize, t],
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    field.onChange(null as typeof field.value);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteMultipleFile = (index: number) => {
    if (!isFileArray(field.value)) return;

    const currentFiles: File[] = field.value;
    if (index < 0 || index >= currentFiles.length) return;

    const fileToRemove = currentFiles[index];
    const newFiles = currentFiles.filter((_: File, i: number) => i !== index);

    // Revoke preview URL
    const url = previewUrls.get(fileToRemove);
    if (url) {
      revokeObjectUrl(url);
      setPreviewUrls((prev) => {
        const newMap = new Map(prev);
        newMap.delete(fileToRemove);
        return newMap;
      });
    }

    // Clean up original image reference
    setOriginalImages((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileToRemove);
      return newMap;
    });

    field.onChange(newFiles as typeof field.value);
  };

  // Manage preview URLs
  React.useEffect(() => {
    if (!multiple) return;
    if (!isFileArray(field.value)) return;

    const files: File[] = field.value;

    setPreviewUrls((prevUrls) => {
      const newPreviewUrls = new Map<File, string>();

      files.forEach((file: File) => {
        const existingUrl = prevUrls.get(file);
        if (existingUrl) {
          newPreviewUrls.set(file, existingUrl);
        } else {
          const url = getServerPreviewUrl(file) ?? URL.createObjectURL(file);
          newPreviewUrls.set(file, url);
        }
      });

      prevUrls.forEach((url, file) => {
        if (!files.includes(file)) {
          revokeObjectUrl(url);
        }
      });

      return newPreviewUrls;
    });
  }, [field.value, multiple]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      previewUrls.forEach((url) => {
        revokeObjectUrl(url);
      });
    };
  }, [previewUrls]);

  // Handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;
    if (!isFileArray(field.value)) return;

    const currentFiles: File[] = field.value;
    const oldIndex = currentFiles.findIndex(
      (file: File) =>
        `${file.name}-${file.size}-${file.lastModified}` === active.id,
    );
    const newIndex = currentFiles.findIndex(
      (file: File) =>
        `${file.name}-${file.size}-${file.lastModified}` === over.id,
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      field.onChange(
        arrayMove(currentFiles, oldIndex, newIndex) as typeof field.value,
      );
    }
  };

  const acceptString =
    accept || allowedFormats.map((f) => `.${f.toLowerCase()}`).join(",");

  const uploadedFile = !multiple && isFile(field.value) ? field.value : null;
  const uploadedFiles =
    multiple && isFileArray(field.value) ? field.value : null;

  return (
    <div className={cn("space-y-2 w-full", containerClass)}>
      <div
        className={cn(
          "border-2 border-dashed rounded-md py-4 px-3 transition-colors",
          isDragging
            ? "border-stroke-primary bg-bg-tertiary/20"
            : error
              ? "border-semantic-error"
              : "border-stroke-light",
          "flex flex-col items-center justify-center gap-4",
          { "border-solid": !!uploadedFile },
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptString}
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
          aria-label={buttonText}
          aria-required={required}
        />
        {uploadedFile ? (
          <div className="w-full">
            <div className="bg-bg-primary rounded-md p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="shrink-0 text-text-primary">{icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary font-medium text-xs truncate">
                    {uploadedFile.name}
                  </p>
                  <p className="text-text-secondary text-[10px]">
                    {formatFileSize(uploadedFile.size)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleDeleteFile}
                className="inline-flex items-center justify-center size-7 text-text-primary transition-colors bg-semantic-error rounded-xs"
                aria-label={t("form.deleteFile")}
              >
                <XIcon size={20} />
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="flex items-center justify-center">{icon}</div>
              <h3 className="text-text-primary font-semibold text-sm text-center">
                {title}
              </h3>
              <p className="text-text-secondary text-xs text-center">
                {t("fileUploader.allowedHint", {
                  formats: formatAllowedFormats(allowedFormats),
                  maxSize,
                })}
              </p>
            </div>
            <Button
              type="button"
              variant="solid"
              colors="secondary"
              onClick={handleButtonClick}
              disabled={loading}
              className="mt-2"
            >
              {buttonText}
            </Button>
          </>
        )}
      </div>
      {!!helperText && (
        <p className="text-xs leading-tight text-muted-foreground ml-1.5">
          {helperText}
        </p>
      )}
      {!!error && (
        <p className="text-xs leading-tight text-semantic-error ml-1.5">
          {error?.message}
        </p>
      )}
      {uploadedFiles && (
        <div className="w-full pt-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={uploadedFiles.map(
                (file: File) =>
                  `${file.name}-${file.size}-${file.lastModified}`,
              )}
            >
              <div ref={imagesContainerRef} className="flex flex-wrap gap-4">
                {uploadedFiles.map((file: File, index: number) => {
                  const fileId = `${file.name}-${file.size}-${file.lastModified}`;
                  return (
                    <SortableImageItem
                      key={fileId}
                      id={fileId}
                      file={file}
                      previewUrl={previewUrls.get(file)}
                      icon={icon}
                      onDelete={() => handleDeleteMultipleFile(index)}
                      showDeleteButton={activeDeleteButtonIndex === index}
                      onShowDeleteButton={() => handleShowDeleteButton(index)}
                      onEdit={
                        isImageUploader && isImageFile(file)
                          ? () => handleEditImage(index)
                          : undefined
                      }
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Multi-Image Crop Dialog */}
      {imagesToCrop.length > 0 && (
        <MultiImageCropDialog
          images={imagesToCrop}
          open={multiCropDialogOpen}
          onOpenChange={(open) => {
            setMultiCropDialogOpen(open);
            if (!open) setImagesToCrop([]);
          }}
          onCropComplete={handleMultiCropComplete}
          cropShape="rect"
          aspectRatio={1}
          title={t("imageCrop.cropImages")}
          maxSizeMB={maxSize}
        />
      )}

      {/* Single Image Edit Dialog */}
      {editingImage && (
        <ImageCropDialog
          image={editingImage.originalUrl}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setEditingImage(null);
          }}
          onCropComplete={handleEditCropComplete}
          cropShape="rect"
          aspectRatio={1}
          title={t("imageCrop.editImage")}
          originalFileName={editingImage.fileName}
          maxSizeMB={maxSize}
        />
      )}
    </div>
  );
}
